/**
 * TeamWhatsAppCard — 2026-10-01, Game Day Stats.
 * ─────────────────────────────────────────────────────────────────
 * Coach/manager card: WhatsApp game updates to parents. Goals, period
 * changes and the final score go to each subscribed parent's WhatsApp as
 * the game is narrated (or as the score is tapped). Parents subscribe by
 * texting "JOIN <code>" to the Game Day WhatsApp number, or the coach adds
 * a number a parent gave them. Lives in the team's Chat section, beside
 * the SMS toggle.
 *
 * Backend: /api/teams/:teamId/whatsapp (GET, PATCH, POST /subscribers,
 * DELETE /subscribers/:id, POST /test).
 */
import { useEffect, useState } from 'react';
import { MessageCircle, CheckCircle2, AlertCircle, Plus, Trash2, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api.js';

export default function TeamWhatsAppCard({ teamId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });

  const load = () => api.get(`/teams/${teamId}/whatsapp`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { let c = false; api.get(`/teams/${teamId}/whatsapp`).then(({ data }) => { if (!c) setData(data); }).catch(() => { if (!c) setData(null); }).finally(() => { if (!c) setLoading(false); }); return () => { c = true; }; }, [teamId]);

  const patch = async (body, okMsg) => {
    if (saving) return;
    setSaving(true);
    try { const { data: r } = await api.patch(`/teams/${teamId}/whatsapp`, body); setData(d => ({ ...d, settings: r.settings })); if (okMsg) toast.success(okMsg); }
    catch (err) { toast.error(err?.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  const addParent = async (e) => {
    e?.preventDefault?.();
    if (!form.phone.trim()) return;
    setSaving(true);
    try { await api.post(`/teams/${teamId}/whatsapp/subscribers`, { phone: form.phone, name: form.name }); toast.success('Added — a welcome message is on its way'); setForm({ name: '', phone: '' }); await load(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Could not add that number'); }
    finally { setSaving(false); }
  };
  const remove = async (sub) => {
    if (!window.confirm(`Stop sending updates to ${sub.name || sub.phone}?`)) return;
    try { await api.delete(`/teams/${teamId}/whatsapp/subscribers/${sub.id}`); await load(); }
    catch { toast.error('Could not remove'); }
  };
  const sendTest = async () => {
    setSaving(true);
    try { const { data: r } = await api.post(`/teams/${teamId}/whatsapp/test`, {}); toast.success(r.sentTo ? `Test sent to ${r.sentTo} parent${r.sentTo === 1 ? '' : 's'}` : 'No subscribed parents yet'); await load(); }
    catch { toast.error('Could not send the test'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="card mb-5"><div className="text-secondary text-xs">Loading WhatsApp settings…</div></div>;
  if (!data) return null;

  const { platform, settings } = data;
  const on = !!settings.is_enabled;
  const subs = (data.subscribers || []).filter(s => s.status === 'subscribed');
  const gone = (data.subscribers || []).filter(s => s.status !== 'subscribed');
  const box = { padding: 14, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10 };

  return (
    <div className="card mb-5">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle size={16} style={{ color: '#25D366' }} />
        <h3 className="font-semibold">WhatsApp game updates</h3>
      </div>
      <p className="text-secondary text-xs mb-4">
        Parents get the goals, period changes and the final score on WhatsApp as the game is tracked — live from the bench
        recorder, or from the score buttons. Each parent gets their own message (WhatsApp does not let an app post into a
        group), and can leave any time by replying STOP.
      </p>

      {!platform?.enabled && (
        <div style={{ ...box, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={14} color="var(--puck)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div className="text-xs text-secondary">WhatsApp isn't connected on this server yet. The Game Day admin sets it up (Meta Business account + a WhatsApp number).</div>
        </div>
      )}

      {platform?.enabled && (<>
        <div style={{ ...box, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ fontSize: 14 }}>{on ? 'Updates are ON' : 'Updates are OFF'}</strong>
              {on && <CheckCircle2 size={14} color="var(--goal)" />}
            </div>
            <div className="text-secondary text-xs" style={{ marginTop: 4 }}>
              {on ? `${subs.length} parent${subs.length === 1 ? '' : 's'} subscribed.` : 'Nothing is sent while this is off; the parent list is kept.'}
            </div>
          </div>
          <button type="button" onClick={() => patch({ is_enabled: !on }, on ? 'WhatsApp updates turned off' : 'WhatsApp updates turned on')} disabled={saving}
            style={{ padding: '7px 16px', background: on ? 'transparent' : '#25D366', color: on ? 'var(--fire)' : '#000', border: `1px solid ${on ? 'var(--fire)' : '#25D366'}`, borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: saving ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
            {saving ? '…' : (on ? 'Turn off' : 'Turn on')}
          </button>
        </div>

        {/* what goes out — 2026-10-17: 'all' is the play-by-play */}
        <div className="flex items-center gap-3 mt-3 text-xs" style={{ flexWrap: 'wrap' }}>
          <span className="text-secondary">Send:</span>
          {[['goals', 'Goals, periods, final score'], ['all', 'Play-by-play: also penalties, power plays, pulled goalies and the official recap']].map(([v, l]) => (
            <label key={v} className="flex items-center gap-1" style={{ cursor: 'pointer' }}>
              <input type="radio" name="wa-level" checked={settings.level === v} onChange={() => { setData(d => ({ ...d, settings: { ...d.settings, level: v } })); patch({ level: v }); }} /> {l}
            </label>
          ))}
        </div>
        {/* 2026-10-17 — check before sending: the voice AI has called goals that never happened.
            2026-10-18: Press Box teams only — that is where the held updates are decided. */}
        {settings.confirm_available && <label className="flex items-start gap-2 mt-2 text-xs" style={{ cursor: 'pointer' }}>
          <input type="checkbox" style={{ marginTop: 2 }} checked={settings.confirm_before_send !== false}
            onChange={(e) => { const v = e.target.checked; setData(d => ({ ...d, settings: { ...d.settings, confirm_before_send: v } })); patch({ confirm_before_send: v }, v ? 'Updates wait for you to tap Send' : 'Updates go out automatically'); }} />
          <span>
            <strong>Check before sending</strong> — each update waits on the live page (and in Press Box) for you to tap Send or Skip.
            <span className="text-secondary"> Turn off to send automatically; the voice AI sometimes hears a goal that wasn't one.</span>
          </span>
        </label>}

        {/* how parents join */}
        <div style={{ ...box, marginTop: 12 }}>
          <div className="text-xs font-bold mb-1">How parents join</div>
          <div className="text-xs text-secondary">
            Text <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>JOIN {settings.join_code}</strong>
            {platform.display ? <> to <strong style={{ color: 'var(--text)' }}>{platform.display}</strong></> : ' to the Game Day WhatsApp number'} on WhatsApp.
            Post that line once in your parents' group and everyone who wants updates subscribes themselves.
          </div>
          <button type="button" className="text-xs mt-2" onClick={() => { navigator.clipboard?.writeText(`Want ${'live'} game updates on WhatsApp? Message "JOIN ${settings.join_code}" to ${platform.display || 'the Game Day WhatsApp number'}. Reply STOP any time to leave.`).then(() => toast.success('Copied — paste it in the parents’ group')); }}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', color: 'var(--ice)', cursor: 'pointer', fontWeight: 600 }}>
            Copy the message for the group
          </button>
        </div>

        {/* parents */}
        <div style={{ ...box, marginTop: 12 }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold">Parents ({subs.length})</div>
            <button type="button" onClick={sendTest} disabled={saving || !on || !subs.length} className="flex items-center gap-1 text-xs"
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', color: 'var(--text-2)', cursor: 'pointer' }}>
              <Send size={11} /> Send a test
            </button>
          </div>
          {subs.length === 0 && <div className="text-xs text-secondary mb-2">Nobody yet. Share the JOIN line above, or add a parent who gave you their number.</div>}
          {subs.map(s => (
            <div key={s.id} className="flex items-center gap-2 text-xs py-1" style={{ borderTop: '1px solid var(--border)' }}>
              <span className="font-semibold" style={{ minWidth: 0, flex: 1 }}>{s.name || 'Parent'} <span className="text-secondary" style={{ fontFamily: 'var(--font-mono)' }}>{s.phone}</span></span>
              <span className="text-secondary">{s.source === 'self' ? 'joined by text' : 'added by coach'}</span>
              {s.last_status === 'failed' && <span title={s.last_error || ''} style={{ color: 'var(--puck)' }}>last message failed</span>}
              {s.last_status === 'read' && <span style={{ color: 'var(--goal)' }}>read ✓✓</span>}
              <button type="button" onClick={() => remove(s)} title="Remove" style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2 }}><Trash2 size={13} /></button>
            </div>
          ))}
          {/* a div, not a <form>: the card sits inside the settings page's own form,
              and a nested form's submit would save the page instead */}
          <div className="flex items-center gap-2 mt-2 flex-wrap" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addParent(e); } }}>
            <input className="input-field" style={{ flex: '1 1 120px', padding: '6px 8px', fontSize: 12 }} placeholder="Parent's name (optional)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={80} />
            <input className="input-field" style={{ flex: '1 1 140px', padding: '6px 8px', fontSize: 12 }} placeholder="Mobile number" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} inputMode="tel" />
            <button type="button" onClick={addParent} disabled={saving || !form.phone.trim()} className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg btn-ice"><Plus size={12} /> Add</button>
          </div>
          <div className="text-xs text-secondary mt-1">Only add a number whose owner asked for the updates — they get a welcome message straight away and can reply STOP.</div>
          {gone.length > 0 && <div className="text-xs text-secondary mt-2">{gone.length} left or removed.</div>}
        </div>
        {data.last30Days && (data.last30Days.sent > 0) && (
          <div className="text-xs text-secondary mt-2">Last 30 days: {data.last30Days.sent} messages{data.last30Days.failed ? `, ${data.last30Days.failed} failed` : ''}.</div>
        )}
      </>)}
    </div>
  );
}
